/**
 * PATH: packages/email-hub/src/feature/components/rich-text/mention.ts
 * FILE_ID: EMAILHUB-RICHTEXT-MENTION-001
 * PURPOSE: @mention extension — type "@name" in the body to insert a chip
 *          AND add that person to the recipient list (operator decision
 *          2026-06-18), reusing the same contact search the To field uses.
 */

import { Mention } from '@tiptap/extension-mention'
import type { Editor } from '@tiptap/core'
import { makeSuggestionRender } from './suggestionRender'
import type { SuggestionController } from './suggestionController'

export interface MentionPerson {
  name: string
  email: string
}

export interface MentionItemsOptions {
  /** Keystroke settle time before the search fires. Matches the To-field's
   *  RecipientAutocomplete DEBOUNCE_MS so the two feel identical. */
  debounceMs?: number
  /** How long a query's results stay servable without a refetch. */
  cacheTtlMs?: number
}

/**
 * I→O factory: (search fn) → the TipTap suggestion `items` function, with the
 * discipline the raw version lacked (BUG-2026-07-07-EMAIL-MENTION-SLOW: the
 * popup "took forever" because EVERY keystroke fired an unthrottled two-backend
 * fetch whose AbortController was never wired, so stale searches raced and the
 * popup waited on the slowest of the pile):
 *   - debounce: only the settled query hits the network
 *   - abort: a new keystroke cancels the in-flight fetch
 *   - supersede: an overtaken caller resolves [] immediately (TipTap ignores
 *     stale item sets; nothing dangles awaiting a cleared timer)
 *   - cache: repeated queries (backspacing, re-typing) answer instantly
 */
export function makeMentionItems(
  search: (query: string, signal: AbortSignal) => Promise<MentionPerson[]>,
  opts: MentionItemsOptions = {},
): (args: { query: string }) => Promise<MentionPerson[]> {
  const debounceMs = opts.debounceMs ?? 180
  const cacheTtlMs = opts.cacheTtlMs ?? 30_000
  const cache = new Map<string, { at: number; items: MentionPerson[] }>()
  let timer: ReturnType<typeof setTimeout> | null = null
  let inflight: AbortController | null = null
  let supersededResolve: ((items: MentionPerson[]) => void) | null = null

  return ({ query }: { query: string }) => {
    const q = (query || '').trim()
    if (q.length < 1) return Promise.resolve([])
    const key = q.toLowerCase()
    const hit = cache.get(key)
    if (hit && Date.now() - hit.at < cacheTtlMs) {
      return Promise.resolve(hit.items)
    }
    if (timer) clearTimeout(timer)
    inflight?.abort()
    supersededResolve?.([])
    return new Promise<MentionPerson[]>((resolve) => {
      supersededResolve = resolve
      timer = setTimeout(async () => {
        if (supersededResolve === resolve) supersededResolve = null
        const ctrl = new AbortController()
        inflight = ctrl
        try {
          const res = await search(q, ctrl.signal)
          const items = res.filter((p) => p.email).slice(0, 8)
          if (!ctrl.signal.aborted) cache.set(key, { at: Date.now(), items })
          resolve(ctrl.signal.aborted ? [] : items)
        } catch {
          resolve([])
        }
      }, debounceMs)
    })
  }
}

export function buildMention(
  controller: SuggestionController,
  search: (query: string, signal: AbortSignal) => Promise<MentionPerson[]>,
  onMention: (person: MentionPerson) => void,
) {
  return Mention.configure({
    HTMLAttributes: { class: 'qte-mention' },
    deleteTriggerWithBackspace: true,
    suggestion: {
      char: '@',
      items: makeMentionItems(search),
      command: ({ editor, range, props }: { editor: Editor; range: { from: number; to: number }; props: unknown }) => {
        const person = props as MentionPerson
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            { type: 'mention', attrs: { id: person.email, label: person.name || person.email } },
            { type: 'text', text: ' ' },
          ])
          .run()
        if (person.email) onMention(person)
      },
      render: () =>
        makeSuggestionRender<MentionPerson>(controller, 'mention', (p) => ({
          id: p.email || p.name,
          title: p.name || p.email,
          hint: p.name ? p.email : undefined,
        })),
    },
  })
}
